
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { 
  ValidationError, 
  validateBusinessInfo, 
  validateOnlinePresence, 
  validateBrandVoice 
} from '@/utils/validationUtils';

interface ValidationContextType {
  errors: ValidationError[];
  validateStep: (step: number, data: any) => boolean;
  getFieldError: (field: string) => string | undefined;
  clearErrors: () => void;
}

const ValidationContext = createContext<ValidationContextType | undefined>(undefined);

export const useValidation = () => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error('useValidation must be used within a ValidationProvider');
  }
  return context;
};

interface ValidationProviderProps {
  children: ReactNode;
}

export const ValidationProvider: React.FC<ValidationProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const validateStep = (step: number, data: any): boolean => {
    let validationResult = { isValid: true, errors: [] as ValidationError[] };
    
    switch (step) {
      case 1:
        validationResult = validateBusinessInfo(data);
        break;
      case 2:
        validationResult = validateOnlinePresence(data);
        break;
      case 3:
        validationResult = validateBrandVoice(data);
        break;
      case 4:
        // Review step doesn't need validation
        validationResult = { isValid: true, errors: [] };
        break;
      default:
        validationResult = { isValid: true, errors: [] };
    }
    
    setErrors(validationResult.errors);
    return validationResult.isValid;
  };

  const getFieldError = (field: string): string | undefined => {
    const error = errors.find(err => err.field === field);
    return error?.message;
  };

  const clearErrors = () => {
    setErrors([]);
  };

  return (
    <ValidationContext.Provider 
      value={{ 
        errors, 
        validateStep, 
        getFieldError,
        clearErrors
      }}
    >
      {children}
    </ValidationContext.Provider>
  );
};
